import { BLOCKS } from '@contentful/rich-text-types';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { newsSlugs } from '@boss/constants/corporate-jobs';
import { RichText } from '@boss/rich-text';
import { NewsArticleFields } from '@boss/types/corporate-jobs';
import { Image, Link, NewsArticleCard, SocialShare } from '@boss/ui';

import ComponentMapper from '../../mappers/ComponentMapper';
import { getNewsArticleProps } from '../../mappers/PropsMapping';

type Props = {
  newsArticle: NewsArticleFields;
  relatedArticles: NewsArticleFields[];
  locale: string;
  metaTitle?: string;
};

const NewsDetailPage = ({ newsArticle, locale, relatedArticles, metaTitle }: Props) => {
  const { publicationDate, author, title, content, image, hideImage } = newsArticle;
  const { t } = useTranslation(['news']);
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    setCurrentPath(window.location.href);
  }, []);

  return (
    <div className="relative mb-20 mt-8">
      <div className="mb-16">
        <div className="mb-8">
          <Link className="cursor-pointer uppercase" href={newsSlugs[locale]}>
            <span className="text-brand">
              <FontAwesomeIcon icon={faChevronLeft} />
            </span>
            <span className="ml-2">{t('toOverview')}</span>
          </Link>
        </div>
        <div className="text-faded mb-10 flex items-center gap-8">
          <span className="annotation">{format(publicationDate, 'dd-MM-yyyy')}</span>
          <span className="annotation uppercase">{author}</span>
          <SocialShare facebook linkedIn title={metaTitle} url={currentPath} xTwitter />
        </div>
        <h1 className="mb-10">
          <RichText
            ComponentMapper={ComponentMapper}
            content={title}
            mapperOptions={{ locale }}
            renderNode={{
              [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
            }}
            renderOptions={{ noWrapper: true }}
          />
        </h1>
        {!hideImage && (
          <Image alt={image?.altText} className="aspect-[9/4] w-full object-cover" src={image?.image?.file?.url} />
        )}
        {content?.map(contentItem => (
          <ComponentMapper entry={contentItem} key={contentItem.id} locale={locale} />
        ))}
        <Link className="flex flex-row items-center gap-4 uppercase" href={newsSlugs[locale]}>
          <FontAwesomeIcon className="text-brand mb-0.5" height={11} icon={faChevronLeft} />
          <span className="button">{t('toOverview')}</span>
        </Link>
      </div>
      {relatedArticles?.length > 0 && (
        <div>
          <h2 className="mb-8">{t('more')}</h2>
          <div className="grid w-full grid-cols-3 gap-12">
            {relatedArticles?.map(article => (
              <NewsArticleCard
                key={article.id}
                translations={{
                  buttonText: t('cardButton'),
                }}
                {...getNewsArticleProps(article, locale)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsDetailPage;
